import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CircleAlertIcon, LoaderCircleIcon } from 'lucide-react';

import { EmptyList } from '@components/EmptyList';
import { PageTitle } from '@components/PageTitle';
import { queryKeys } from '@constants/queryKeys.constant';
import { smartMatchService } from '@services/smartMatches/service';

import { SmartMatchAssignForm } from './@components/SmartMatch/AssignForm';

function smartMatchInterventionsQueryOptions(serviceRequestId: string) {
  return queryOptions({
    queryKey: queryKeys.smartMatches.interventions(serviceRequestId),
    queryFn: () => smartMatchService.getInterventions({ serviceRequestId }),
  });
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/smart-match',
)({
  component: ServiceRequestSmartMatch,
  loader: ({ context, params }) => {
    const options = smartMatchInterventionsQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => (
    <div className="flex h-full items-center justify-center">
      <LoaderCircleIcon className="text-primary animate-spin" size={80} />
    </div>
  ),
  pendingMs: 300,
  pendingMinMs: 600,
});

function ServiceRequestSmartMatch() {
  const { id: serviceRequestId } = Route.useParams();

  const { data } = useSuspenseQuery(
    smartMatchInterventionsQueryOptions(serviceRequestId),
  );

  return (
    <>
      <PageTitle
        hasBackButton
        params={{ id: serviceRequestId }}
        title="SmartMatch"
        to="/service-requests/$id"
      />

      <div className="bg-warning mt-2 -mr-8 mb-6 ml-auto flex h-16 max-w-1/2 items-center gap-x-4 rounded-l-lg px-6 text-white">
        <CircleAlertIcon size={24} strokeWidth={1.5} />
        <span className="flex-1 leading-none">
          Have you created all the interventions needed for this service
          request? SmartMatch will rank providers based on all existing
          interventions
        </span>
      </div>

      <EmptyList
        alt="No interventions to match animation/image"
        description="Every intervention of this service request already has a provider."
        isVisible={!data.matches.length}
        title="All interventions are assigned."
      />

      {!!data.matches.length && (
        <SmartMatchAssignForm
          key={data.matches.map(match => match.intervention.id).join('|')}
          matches={data.matches}
          serviceRequestId={serviceRequestId}
        />
      )}
    </>
  );
}
